import { useEffect, useState } from "react";
import { API_URL } from "../../env";
import SmartImage from "../components/ui/SmartImage";

// Detail view for an individual product type, shown inside the modal when a
// type card is clicked. It keeps the same dialog chrome (back button on the
// image, Enquire footer) so navigation feels consistent.
const TypeDetail = ({ type, productTitle, onBack, onEnquire }) => {
  const imgUrl = `${API_URL}${type.image}`;

  return (
    <>
      <div className="relative h-56 w-full shrink-0 overflow-hidden md:h-64">
        <SmartImage
          src={imgUrl}
          alt={type.name}
          wrapperClassName="h-full w-full"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/10 to-transparent" />
        <button
          type="button"
          onClick={onBack}
          className="absolute left-4 top-4 z-10 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-md transition-colors hover:text-slate-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to {productTitle}
        </button>
        <div className="absolute bottom-4 left-5 right-14">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-300">
            Product Type
          </p>
          <h3
            className="text-xl font-bold text-white drop-shadow-md md:text-2xl"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {type.name}
          </h3>
        </div>
      </div>

      <div className="overflow-y-auto px-6 py-5">
        <p className="text-sm leading-relaxed text-slate-600 md:text-base">
          {type.description || "No additional details provided for this type."}
        </p>
      </div>

      <div className="shrink-0 border-t border-slate-100 px-6 py-4">
        <button
          type="button"
          onClick={() => onEnquire(`${productTitle} - ${type.name}`)}
          className="w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-200 transition-colors hover:bg-blue-700"
        >
          Enquire About {type.name}
        </button>
      </div>
    </>
  );
};

const LearnMore = ({ product, onClose, onEnquire }) => {
  const [selectedType, setSelectedType] = useState(null);

  // While a type detail is open, Escape goes back to the product view instead
  // of closing the whole dialog. stopImmediatePropagation keeps the parent
  // (ProductsSection) Escape handler from closing the dialog at the same time.
  useEffect(() => {
    if (!selectedType) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
        setSelectedType(null);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedType]);

  if (!product) return null;

  const { title, description, detailedDescription, image, listOfProducts = [] } = product;
  const imgUrl = `${API_URL}${image}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={selectedType ? selectedType.name : title}
      className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
    >
      <button
        type="button"
        aria-label="Close details"
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-md transition-colors hover:text-slate-800"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {selectedType ? (
        <TypeDetail
          type={selectedType}
          productTitle={title}
          onBack={() => setSelectedType(null)}
          onEnquire={onEnquire}
        />
      ) : (
        <>

      <div className="relative h-56 w-full shrink-0 overflow-hidden md:h-64">
        <SmartImage
          src={imgUrl}
          alt={title}
          wrapperClassName="h-full w-full"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-4 left-5 right-14">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-300">{description}</p>
          <h2
            className="text-xl font-bold text-white drop-shadow-md md:text-2xl"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {title}
          </h2>
        </div>
      </div>

      <div className="overflow-y-auto px-6 py-5">
        <p className="text-sm leading-relaxed text-slate-600 md:text-base">
          {detailedDescription}
        </p>

        {listOfProducts.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Available Types ({listOfProducts.length})
            </p>
            <p className="mt-1 mb-3 text-xs text-blue-600">
              Select a type below to view its details.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {listOfProducts.map((item) => (
                <button
                  type="button"
                  key={item._id}
                  onClick={() => setSelectedType(item)}
                  className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50"
                >
                  <SmartImage
                    src={`${API_URL}${item.image}`}
                    alt={item.name}
                    wrapperClassName="h-12 w-12 shrink-0 rounded-lg"
                    className="h-12 w-12 rounded-lg object-cover"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800 group-hover:text-blue-700">
                      {item.name}
                    </p>
                    <p className="line-clamp-1 text-xs text-slate-500">
                      {item.description}
                    </p>
                    <p className="mt-1 text-[11px] font-semibold text-blue-600">
                      Click to know more
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

        <div className="shrink-0 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={() => onEnquire(title)}
            className="w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-200 transition-colors hover:bg-blue-700"
          >
            Enquire About {title}
          </button>
        </div>
        </>
      )}
    </div>
  );
};

export default LearnMore;